import {
  QualityControlFlag,
  QualityControlOutput,
  getQualityControlFlags,
  getQualityControlReviewKey,
  resolveQualityControlFlag,
} from '@aloft/toolkit-shared';

import { useCallback } from 'react';

const isSameFlag = (left: QualityControlFlag, right: QualityControlFlag): boolean =>
  left.category === right.category &&
  left.section === right.section &&
  left.description === right.description;

export const useResolveQualityControlFlag = (
  qualityControl: QualityControlOutput,
  updateQualityControl: (body: QualityControlOutput) => Promise<QualityControlOutput>,
) => {
  const resolveFlag = useCallback(
    async (flag: QualityControlFlag) => {
      const reviewKey = getQualityControlReviewKey(qualityControl, flag);
      if (!reviewKey) return;

      const isStillPending = getQualityControlFlags(qualityControl).some(
        (candidate) => candidate.status === 'pending' && isSameFlag(candidate, flag),
      );
      if (!isStillPending) return;

      const updatedQualityControl = resolveQualityControlFlag(qualityControl, reviewKey, flag);
      await updateQualityControl(updatedQualityControl);
    },
    [qualityControl, updateQualityControl],
  );

  const resolveFlags = useCallback(
    async (flags: QualityControlFlag[]) => {
      const pendingFlags = flags.filter((flag) =>
        getQualityControlFlags(qualityControl).some(
          (candidate) => candidate.status === 'pending' && isSameFlag(candidate, flag),
        ),
      );
      if (pendingFlags.length === 0) return;

      const updatedQualityControl = pendingFlags.reduce((output, flag) => {
        const reviewKey = getQualityControlReviewKey(output, flag);
        return reviewKey ? resolveQualityControlFlag(output, reviewKey, flag) : output;
      }, qualityControl);
      await updateQualityControl(updatedQualityControl);
    },
    [qualityControl, updateQualityControl],
  );

  return { resolveFlag, resolveFlags };
};
